import { del, get, patch, post } from "@/infra/rest";

export interface Questionnaire {
  questionnaire_name: string;
  questionnaire_url: string;
  id: string;
  created_by: string;
  created_on: string;
  updated_by: string;
  updated_on: string;
  number_of_questions: number;
  number_of_evidences: number;
  last_updated_by: string;
  last_updated_on: string;
}

export interface QuestionnaireResponse {
  success: boolean;
  questionnaires: Questionnaire[];
}

export interface DeleteQuestionnaireResponse {
  success: boolean;
  message?: string;
}

export interface RenameQuestionnairePayload {
  update_type: "rename";
  current_questionnaire_name: string;
  new_questionnaire_name: string;
}

export interface RenameQuestionnaireResponse {
  success: boolean;
  questionnare: {
    id: string;
    organization_id: string;
    questionnaire_name: string;
    questions: any[];
    number_of_questions: number;
    questionnaire_json_url: string | null;
    questions_document_url: string | null;
    type: string;
    created_on: string;
    created_by: string;
    last_updated_by: string;
    last_updated_on: string;
    published: boolean;
  };
}

export interface CreateQuestionnaireResponse {
  success: boolean;
  questionnaire_state: Questionnaire;
  message?: string;
}

export const getQuestionnaires = async (): Promise<QuestionnaireResponse> => {
  return get<QuestionnaireResponse>({
    url: '/v0/tpra/questionnaires',
    isAuthRequired: true,
  });
};

export const getPublishedQuestionnaires = async (): Promise<QuestionnaireResponse> => {
  return get<QuestionnaireResponse>({
    url: '/v0/tpra/questionnaires?filter=published',
    isAuthRequired: true,
  });
};


export const deleteQuestionnaire = async (questionnaireId: string): Promise<DeleteQuestionnaireResponse> => {
  return del<DeleteQuestionnaireResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}`,
    isAuthRequired: true,
  });
};

export const renameQuestionnaire = async (
  questionnaireId: string,
  newName: string,
  currentName: string
): Promise<RenameQuestionnaireResponse> => {
  const payload: RenameQuestionnairePayload = {
    update_type: "rename",
    current_questionnaire_name: currentName,
    new_questionnaire_name: newName,
  };

  return patch<RenameQuestionnaireResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}`,
    config: { data: payload },
    isAuthRequired: true,
  });
};

export interface QuestionPayload {
  question: string;
  question_type: "free_text" | "single_choice" | "multiple_choice";
  is_dropdown: boolean;
  response_choices: string[];
  required: boolean;
  comments_allowed: boolean;
  parent_question_id?: string;
  parent_question_response_option?: string;
}

export interface AddQuestionPayload {
  question: QuestionPayload;
}

export interface QuestionResponse {
  question: string;
  question_type: "free_text" | "single_choice" | "multiple_choice";
  is_dropdown: boolean;
  response_choices: string[];
  required: boolean;
  comments_allowed: boolean;
  parent_question_id?: string;
  parent_question_response_option?: string;
  id: string;
  question_number: string;
}

export interface AddQuestionResponse {
  success: boolean;
  question: QuestionResponse;
  questions_url: string;
}

export interface UpdateQuestionPayload {
  question: QuestionPayload;
}

export interface UpdateQuestionResponse {
  success: boolean;
  question: QuestionResponse;
  questions_url: string;
}

export interface CreateQuestionPayload {
  questionnaire_id: string;
  question: string;
  question_type: string;
  response_choices: string[];
  required: boolean;
  parentQuestion?: string | null;
  parentQuestionOption?: string | null;
}

export interface CreateQuestionResponse {
  success: boolean;
  question_id?: string;
  message?: string;
}

export const createQuestion = async (payload: CreateQuestionPayload): Promise<CreateQuestionResponse> => {
  return post<CreateQuestionPayload, CreateQuestionResponse>({
    url: '/v0/tpra/questionnaires/create',
    data: payload,
    isAuthRequired: true,
  });
};
export interface Evidence {
  id: string;
  document_name: string;
  required: boolean;
  max_number_of_files: number;
  rules: string[];
  created_by: string;
  created_on: string;
  updated_on: string;
}

export interface GetEvidencesResponse {
  success: boolean;
  evidences: Evidence[];
}

export interface AddEvidencePayload {
  name: string;
  required: boolean;
  max_files: number;
  rules: string[];
}

export interface AddEvidenceResponse {
  success: boolean;
  evidence: Evidence;
  message?: string;
}

export const getQuestionnaireEvidences = async (questionnaireId: string): Promise<GetEvidencesResponse> => {
  return get<GetEvidencesResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/evidences`,
    isAuthRequired: true,
  });
};

export const addQuestionnaireEvidence = async (
  questionnaireId: string,
  payload: AddEvidencePayload
): Promise<AddEvidenceResponse> => {
  return post<AddEvidencePayload, AddEvidenceResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/evidences/add`,
    data: payload,
    isAuthRequired: true,
  });
};

export const addQuestionToQuestionnaire = async (
  questionnaireId: string,
  payload: AddQuestionPayload
): Promise<AddQuestionResponse> => {
  return post<AddQuestionPayload, AddQuestionResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/question/add`,
    data: payload,
    isAuthRequired: true,
  });
};

export const updateQuestion = async (
  questionnaireId: string,
  questionId: string,
  payload: UpdateQuestionPayload
): Promise<UpdateQuestionResponse> => {
  return patch<UpdateQuestionResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/question/${questionId}`,
    config: { data: payload },
    isAuthRequired: true,
  });
};
export interface UpdateEvidencePayload {
  name: string;
  max_files: number;
  rules: string[];
  required: boolean;
}

export interface UpdateEvidenceResponse {
  success: boolean;
  evidence: {
    id: string;
    document_name: string;
    required: boolean;
    max_number_of_files: number;
    rules: string[];
    created_by: string;
    created_on: string;
    updated_on: string;
  };
}

export const updateQuestionnaireEvidence = async (
  questionnaireId: string,
  evidenceId: string,
  payload: UpdateEvidencePayload
): Promise<UpdateEvidenceResponse> => {
  return patch<UpdateEvidenceResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/evidences/${evidenceId}`,
    config: { data: payload },
    isAuthRequired: true,
  });
};

export interface DeleteQuestionResponse {
  success: boolean;
  message?: string;
}

export const deleteQuestion = async (
  questionnaireId: string,
  questionId: string
): Promise<DeleteQuestionResponse> => {
  return del<DeleteQuestionResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/question/${questionId}`,
    isAuthRequired: true,
  });
};
export interface DeleteEvidenceResponse {
  success: boolean;
  message: string;
}

export const deleteQuestionnaireEvidence = async (
  questionnaireId: string,
  evidenceId: string
): Promise<DeleteEvidenceResponse> => {
  return del<DeleteEvidenceResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/evidences/${evidenceId}`,
    isAuthRequired: true,
  });
};

export interface RefineValidationRulesPayload {
  rules: string[];
}

export interface RefineValidationRulesResponse {
  success: boolean;
  rules: string[];
}

export const refineValidationRules = async (
  payload: RefineValidationRulesPayload
): Promise<RefineValidationRulesResponse> => {
  return post<RefineValidationRulesPayload, RefineValidationRulesResponse>({
    url: '/v0/tpra/questionnaires/evidences/refine-rules',
    data: payload,
    isAuthRequired: true,
  });
};

export interface CreateQuestionnairePayload {
  questionnaire_name: string;
  type: "multi_file_vendor_assessment";
}

export interface CreateQuestionnaireApiResponse {
  success: boolean;
  questionnaire: {
    id: string;
    organization_id: string;
    questionnaire_name: string;
    questions: any[];
    number_of_questions: number;
    questionnaire_json_url: string | null;
    questions_document_url: string | null;
    type: "multi_file_vendor_assessment";
    created_on: string;
    created_by: string;
    last_updated_by: string;
    last_updated_on: string;
    published: boolean;
  };
}

export const createQuestionnaire = async (name: string): Promise<CreateQuestionnaireApiResponse> => {
  const payload: CreateQuestionnairePayload = {
    questionnaire_name: name,
    type: "multi_file_vendor_assessment",
  };

  return post<CreateQuestionnairePayload, CreateQuestionnaireApiResponse>({
    url: '/v0/tpra/questionnaires/create',
    data: payload,
    isAuthRequired: true,
  });
};

export interface PublishQuestionnaireResponse {
  success: boolean;
  message?: string;
}

export const publishQuestionnaire = async (questionnaireId: string): Promise<PublishQuestionnaireResponse> => {
  return post<{}, PublishQuestionnaireResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/publish`,
    data: {},
    isAuthRequired: true,
  });
};

export interface BulkAddQuestionPayload {
  question_number: string;
  question: string;
  question_type: "free_text" | "single_choice" | "multiple_choice";
  is_dropdown: boolean;
  response_choices: string[];
  required: boolean;
  comments_allowed: boolean;
  parent_question_id?: string;
  parent_question_response_option?: string;
}

export interface BulkAddQuestionsPayload {
  questions: BulkAddQuestionPayload[];
}

export interface BulkAddQuestionsResponse {
  success: boolean;
  questions: QuestionResponse[];
  message?: string;
}

export const bulkAddQuestions = async (
  questionnaireId: string,
  payload: BulkAddQuestionsPayload
): Promise<BulkAddQuestionsResponse> => {
  return post<BulkAddQuestionsPayload, BulkAddQuestionsResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/questions/add`,
    data: payload,
    isAuthRequired: true,
  });
};

// New Save Questions API
export interface SaveQuestionPayload {
  question_number: string;
  question: string;
  question_type: "free_text" | "single_choice" | "multiple_choice";
  is_dropdown: boolean;
  response_choices: string[];
  required: boolean;
  comments_allowed: boolean;
  parent_question_type?: "free_text" | "single_choice" | "multiple_choice";
  // parent_question_response_option removed - backend doesn't need it, uses question_number to determine relationships
}

export interface SaveQuestionsPayload {
  questions: SaveQuestionPayload[];
}

export interface SaveQuestionsResponse {
  success: boolean;
  questions: QuestionResponse[];
  message?: string;
}

export const saveQuestions = async (
  questionnaireId: string,
  payload: SaveQuestionsPayload
): Promise<SaveQuestionsResponse> => {
  return post<SaveQuestionsPayload, SaveQuestionsResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/questions/save`,
    data: payload,
    isAuthRequired: true,
  });
};

// Extract questions from document
export interface ExtractQuestionsPayload {
  document_url: string;
}

export interface ExtractQuestionsResponse {
  success: boolean;
  message: string;
}

export const extractQuestionsFromDocument = async (
  questionnaireId: string,
  payload: ExtractQuestionsPayload
): Promise<ExtractQuestionsResponse> => {
  return post<ExtractQuestionsPayload, ExtractQuestionsResponse>({
    url: `/v0/tpra/questionnaires/${questionnaireId}/extract-questions`,
    data: payload,
    isAuthRequired: true,
  });
};